/**
 * Logic for the register page
 */

import Pristine from 'pristinejs';
import { showError, showSuccess, updateInputs } from '../forms';
import { setLoading, wait, activateFloatingLabel } from '../utils';
import EcommerceAnalytics from '../ecommerce-analytics';
import { getAccountProviderDisplayName } from '../../../../app/Libraries/Auth0/Auth0Utils';
import { populateRegions, Country } from '../country-select';
import type { EnhancedConversionData } from '../../../../app/Types/Analytics';
import { getPhoneDigits } from '../../../../app/Libraries/PhoneUtils';

/** Google wants ISO 3166-1 alpha-2; the form's country select carries display names. */
const COUNTRY_CODES: Record<string, string> = {
  Canada: 'CA',
  USA: 'US',
};

/**
 * Pull the identity fields Google uses to match a conversion back to an ad click.
 * Everything is optional on Google's side, so a missing field just weakens matching
 * rather than breaking the conversion. Values are sent raw; gtag hashes them.
 */
function buildEnhancedConversionData(formData: FormData): EnhancedConversionData {
  const text = (field: string) => {
    const value = formData.get(field);
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  };

  const fullName = text('fullName');
  const [firstName, ...rest] = fullName ? fullName.split(' ') : [];
  const country = text('country');

  // Google requires E.164 (+15195551234). The form carries dashed NANP (519-555-1234) and the
  // site is NANP-only, so +1 is always the right country code. Anything that does not reduce
  // to 10 digits is dropped rather than sent malformed.
  const phoneDigits = getPhoneDigits(text('phone'));

  return {
    email: text('email'),
    phone_number: phoneDigits ? `+1${phoneDigits}` : undefined,
    address: {
      first_name: firstName || undefined,
      last_name: rest.length ? rest.join(' ') : undefined,
      street: text('address1'),
      city: text('city'),
      region: text('province'),
      postal_code: text('postalCode'),
      country: country ? (COUNTRY_CODES[country] ?? country) : undefined,
    },
  };
}

document.addEventListener('DOMContentLoaded', async () => {
  const form = document.getElementById('registerForm') as HTMLFormElement | null;
  if (!form) {
    return;
  };

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

  // Disable SMS notification option if they don't have a phone number added
  const phoneInput = form.querySelector<HTMLInputElement>('input[name="phone"]');
  const smsInput = form.querySelector<HTMLInputElement>('input[name="optInSms"]');
  if (phoneInput && smsInput) {
    phoneInput.addEventListener('input', () => {
      if (phoneInput.value) {
        smsInput.removeAttribute('disabled');
      } else {
        smsInput.checked = false;
        smsInput.setAttribute('disabled', 'true');
      }
    });
  }

  // Bind country -> province/state population, and prefill the province for an existing
  // customer's address (the value is carried on the select's data-selected attribute).
  const countrySelect = form.querySelector<HTMLSelectElement>('select[name="country"]');
  const provinceSelect = form.querySelector<HTMLSelectElement>('select[name="province"]');
  if (countrySelect && provinceSelect) {
    const presetProvince = provinceSelect.dataset.selected || '';

    countrySelect.addEventListener('change', () => {
      // Changing country resets the province list; no preset on a manual change.
      populateRegions(form, countrySelect.value as Country);
    });

    // Initial population, selecting the prefilled province when present.
    populateRegions(form, countrySelect.value as Country, presetProvince);

    // Safety net: if the incoming province isn't in our region list, keep it as an
    // option anyway so the prefilled value still validates and submits.
    if (presetProvince && !provinceSelect.value) {
      provinceSelect.add(new Option(presetProvince, presetProvince, true, true));
      // populateRegions() activates the floating label itself, but this manual path
      // bypasses it — re-activate so the label doesn't overlap the selected value.
      activateFloatingLabel(provinceSelect);
    }
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const honey = form.querySelector<HTMLInputElement>('input[name="age"]');
    const pristine = new Pristine(form, {
      classTo: 'form__input-wrap',
      errorTextParent: 'form__input-wrap',
    });

    const valid = pristine.validate() && !honey?.value;

    if (valid) {
      // Submit form
      const formData = new FormData(form);
      formData.append('_csrf', csrfToken as string);

      updateInputs(form, true); // disable inputs
      setLoading(form, true);

      try {
        const response = await fetch('/register', {
          method: 'POST',
          credentials: 'same-origin',
          // Ask for JSON so Adonis returns/validates against the JSON (API) error shape.
          headers: { Accept: 'application/json' },
          body: formData,
        });

        // Test endpoint
        // const response = await fetch('/api/test', {
        //   credentials: 'same-origin',
        // });
        // await wait(3000);

        // const json = await response.json();
        // console.debug(json);

        if (!response.ok) {
          showError(form);
          updateInputs(form, false); // enable inputs
          setLoading(form, false);
          return;
        }

        const json = await response.json();

        // If the server detected the account already exists, skip straight to login.
        if (json.redirect) {
          window.location.href = json.redirect;
          return;
        }

        showSuccess(form);
        setLoading(form, false);

        // Fire 'sign up' analytics event. This doubles as a Google Ads conversion
        // (supporting; purchase is the main one), so pass the identity fields the form
        // already collected for enhanced conversions matching. gtag hashes them in the browser.
        const eCommerceAnalytics = new EcommerceAnalytics();
        const method = getAccountProviderDisplayName(form.dataset.auth0Id ?? '');
        eCommerceAnalytics.trackSignUp(method, buildEnhancedConversionData(formData));

        // Add a delay so they can read the success message
        await wait(3000);

        // The user is already logged in (the session was established back in the Auth0 callback),
        // so go straight to the post-success destination the server sent in `next` (the dashboard),
        // skipping the redundant /login -> Auth0 round-trip. `/login` stays only as a route-agnostic
        // fallback for the shouldn't-happen case of a 200 without `next`: an already-authenticated
        // user hitting /login simply bounces through Auth0 back to the dashboard.
        window.location.href = json.next || '/login';
      } catch (error) {
        console.error(error);
        // Generic "error submitting form" message
        showError(form);
        // enable inputs
        // updateInputs(form, false);
        setLoading(form, false);
      }
    }
  });
});
